if (!customElements.get('beds-variant-selector')) {
  customElements.define(
    'beds-variant-selector',
    class BedsVariantSelector extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[data-beds-tab]'));
        this.panels = Array.from(this.querySelectorAll('[data-beds-panel]'));

        this.tabs.forEach((tab) => tab.addEventListener('click', this.onTabClick.bind(this)));

        this.addEventListener('change', this.onBaseOptionChange.bind(this));
        this.addEventListener('change', this.onSizeOptionChange.bind(this));
        this.addEventListener('click', this.onColourSwatchClick.bind(this));

        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.onVariantChange.bind(this)
        );

        this.restoreSelectedBaseFromUrl();

        // On mobile the Colour tab is hidden (colour swatches render above the
        // tabs instead), so default to the Size tab rather than the Colour tab.
        if (window.matchMedia('(max-width: 749px)').matches) {
          this.activateTab('size');
        }

        const initialSizeInput = this.querySelector(
          '.beds-variant-selector__option-grid--size .beds-variant-selector__option-input:checked'
        );
        if (initialSizeInput) this.syncMattressAddonToSize(initialSizeInput);

        // Runs on page load and after a colour swap (the swap inserts a fresh copy of this
        // element), whose scroll containers start at the top/left again. Wait a frame so the
        // replaced markup is hidden and layout is settled before measuring.
        requestAnimationFrame(() => this.scrollSelectedSwatchIntoView());
      }

      // Centres the selected colour swatch inside its scrollable panel/grid (vertical on
      // desktop, horizontal on mobile). Adjusts those containers directly rather than using
      // scrollIntoView(), which would also scroll the page.
      scrollSelectedSwatchIntoView() {
        const swatch = this.querySelector('.beds-variant-selector__swatch[data-selected="true"]');
        if (!swatch) return;

        for (let element = swatch.parentElement; element; element = element.parentElement) {
          const { overflowX, overflowY } = getComputedStyle(element);
          const swatchRect = swatch.getBoundingClientRect();
          const elementRect = element.getBoundingClientRect();

          if (/auto|scroll/.test(overflowY) && element.scrollHeight > element.clientHeight) {
            element.scrollTop += swatchRect.top - elementRect.top - (element.clientHeight - swatchRect.height) / 2;
          }
          if (/auto|scroll/.test(overflowX) && element.scrollWidth > element.clientWidth) {
            element.scrollLeft += swatchRect.left - elementRect.left - (element.clientWidth - swatchRect.width) / 2;
          }

          if (element === this) break;
        }
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
      }

      onTabClick(event) {
        this.activateTab(event.currentTarget.dataset.bedsTab);
      }

      activateTab(target) {
        this.tabs.forEach((tab) => tab.setAttribute('aria-selected', tab.dataset.bedsTab === target ? 'true' : 'false'));
        this.panels.forEach((panel) => {
          panel.toggleAttribute('hidden', panel.dataset.bedsPanel !== target);
        });

        // A hidden panel can't be scrolled, so re-centre the swatch once the Colour tab is shown.
        if (target === 'colour') this.scrollSelectedSwatchIntoView();
      }

      onBaseOptionChange(event) {
        const input = event.target.closest('.beds-variant-selector__option-grid--base .beds-variant-selector__option-input');
        if (!input) return;

        this.querySelectorAll('.beds-variant-selector__option-grid--base .beds-variant-selector__option').forEach(
          (option) => option.classList.remove('is-selected')
        );
        input.nextElementSibling?.classList.add('is-selected');

        const pill = this.querySelector('[data-tab-value="base"]');
        if (pill) pill.textContent = input.dataset.baseTitle || '-';

        this.dispatchEvent(new CustomEvent('beds:selection-change', { bubbles: true }));
      }

      onSizeOptionChange(event) {
        const input = event.target.closest('.beds-variant-selector__option-grid--size .beds-variant-selector__option-input');
        if (!input) return;

        const selectedSizeValue = input.value;

        this.querySelectorAll('.beds-variant-selector__swatch[data-variant-urls]').forEach((swatch) => {
          let urlMap;
          try {
            urlMap = JSON.parse(swatch.dataset.variantUrls || '{}');
          } catch (error) {
            return;
          }

          swatch.dataset.productUrl = urlMap[selectedSizeValue] || swatch.dataset.defaultUrl || swatch.dataset.productUrl;
        });

        this.syncMattressAddonToSize(input);
      }

      // The Mattress add-on's variants are set up in the same order as the bed's Size
      // options (1st size <-> 1st mattress variant, etc.), so changing the bed size keeps
      // the mattress selection aligned by position rather than by matching titles.
      syncMattressAddonToSize(input) {
        const sizeInputs = Array.from(
          this.querySelectorAll('.beds-variant-selector__option-grid--size .beds-variant-selector__option-input')
        );
        const index = sizeInputs.indexOf(input);
        if (index === -1) return;

        // beds-variant-selector's own connectedCallback (which triggers the initial sync)
        // can run before beds-addons.js has registered the <beds-addons> custom element,
        // since script execution order isn't the same as DOM order. Waiting for the
        // definition guarantees selectVariantAtIndex exists instead of silently no-oping.
        customElements.whenDefined('beds-addons').then(() => {
          const addons = document.querySelector(`beds-addons[data-section-id="${this.dataset.section}"]`);
          addons?.selectVariantAtIndex?.('mattress', index);
        });
      }

      async onColourSwatchClick(event) {
        const target = event.target.closest('.beds-variant-selector__swatch');
        if (!target || target.dataset.selected === 'true') return;

        const productUrl = target.dataset.productUrl;
        if (!productUrl) return;

        const productInfo = this.closest('product-info');
        if (!productInfo || productInfo.dataset.swappingColour === 'true') {
          if (!productInfo) window.location.href = productUrl;
          return;
        }

        const destination = new URL(productUrl, window.location.origin);

        new URLSearchParams(window.location.search).forEach((value, key) => {
          if (key === 'variant' || destination.searchParams.has(key)) return;
          destination.searchParams.set(key, value);
        });

        const selectedBase = this.querySelector(
          '.beds-variant-selector__option-grid--base .beds-variant-selector__option-input:checked'
        );
        if (selectedBase?.dataset.baseHandle) {
          destination.searchParams.set('base', selectedBase.dataset.baseHandle);
        }

        const requestUrl = new URL(destination.toString());
        requestUrl.searchParams.set('section_id', this.dataset.section);

        const retained = this.captureRetainedSelections(productInfo);
        productInfo.dataset.swappingColour = 'true';
        const overlay = this.showLoadingOverlay(productInfo);

        try {
          const response = await fetch(requestUrl.toString());
          if (!response.ok) throw new Error(`Section request failed with status ${response.status}`);

          const responseText = await response.text();
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const newProductInfo = html.querySelector('product-info');
          if (!newProductInfo) throw new Error('Section response did not include product-info markup');

          HTMLUpdateUtility.viewTransition(productInfo, newProductInfo);
          const liveProductInfo = productInfo.previousElementSibling;

          this.applyRetainedSelections(liveProductInfo, retained);
          window.positionProductTestimonials?.(this.dataset.section);
          window.history.pushState({}, '', destination.toString());
        } catch (error) {
          console.error(error);
          window.location.href = destination.toString();
          return;
        } finally {
          overlay?.remove();
        }
      }

      captureRetainedSelections(productInfo) {
        const baseInput = this.querySelector(
          '.beds-variant-selector__option-grid--base .beds-variant-selector__option-input:checked'
        );
        const addons = productInfo.querySelector('beds-addons');
        const addonSelections = addons
          ? Array.from(addons.querySelectorAll('[data-addon-row]'))
              .filter((row) => row.dataset.selectedVariantId)
              .map((row) => ({
                addonId: row.dataset.addonId,
                variantId: row.dataset.selectedVariantId,
                price: row.dataset.selectedPrice,
                // The row's own markup already shows the picked variant, so read the display
                // straight from it rather than re-deriving it from a (possibly different) DOM.
                image: row.querySelector('[data-addon-variant-image]')?.src || null,
                productTitle: row.querySelector('[data-addon-product-title]')?.textContent || '',
                variantTitle: row.querySelector('[data-addon-variant-name]')?.textContent || '',
                priceFormatted: row.querySelector('[data-addon-variant-price]')?.textContent || '',
              }))
          : [];
        const quantityInput = productInfo.querySelector('.quantity__input');

        return {
          baseHandle: baseInput?.dataset.baseHandle || null,
          addonSelections,
          quantityValue: quantityInput?.value || null,
        };
      }

      applyRetainedSelections(productInfo, retained) {
        if (!productInfo) return;

        if (retained.baseHandle) {
          const baseInput = productInfo.querySelector(
            `.beds-variant-selector__option-grid--base .beds-variant-selector__option-input[data-base-handle="${retained.baseHandle}"]`
          );
          if (baseInput && !baseInput.checked) {
            baseInput.checked = true;
            baseInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        const addons = productInfo.querySelector('beds-addons');
        retained.addonSelections.forEach(({ addonId, variantId, price, image, productTitle, variantTitle, priceFormatted }) => {
          const row = addons?.querySelector(`[data-addon-row][data-addon-id="${addonId}"]`);
          if (!row || typeof addons.selectRow !== 'function') return;
          addons.selectRow(row, variantId, price, { image, productTitle, variantTitle, priceFormatted });
        });

        if (retained.quantityValue) {
          const quantityInput = productInfo.querySelector('.quantity__input');
          if (quantityInput) quantityInput.value = retained.quantityValue;
        }
      }

      showLoadingOverlay(productInfo) {
        const template = this.querySelector('[data-beds-color-swap-overlay-template]');
        const overlay = template?.content.firstElementChild.cloneNode(true);
        if (!overlay) return null;

        productInfo.appendChild(overlay);
        requestAnimationFrame(() => overlay.classList.add('is-visible'));
        return overlay;
      }

      restoreSelectedBaseFromUrl() {
        const baseHandle = new URLSearchParams(window.location.search).get('base');
        if (!baseHandle) return;

        const input = this.querySelector(
          `.beds-variant-selector__option-grid--base .beds-variant-selector__option-input[data-base-handle="${baseHandle}"]`
        );
        if (!input || input.checked) return;

        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      getSelectedItems() {
        const input = this.querySelector(
          '.beds-variant-selector__option-grid--base .beds-variant-selector__option-input:checked'
        );
        const variantId = input?.dataset.baseVariantId;
        return variantId ? [{ id: variantId, quantity: 1 }] : [];
      }

      getSelectedBasePrice() {
        const input = this.querySelector(
          '.beds-variant-selector__option-grid--base .beds-variant-selector__option-input:checked'
        );
        return Number(input?.dataset.basePrice) || 0;
      }

      onVariantChange({ data: { sectionId, variant } }) {
        if (sectionId !== this.dataset.section || !variant) return;

        const position = this.dataset.sizeOptionPosition;
        if (!position) return;

        const pill = this.querySelector('[data-tab-value="size"]');
        if (pill) pill.textContent = variant[`option${position}`] || '-';
      }
    }
  );
}
